"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, ArrowRight, CalendarClock } from "lucide-react";
import {
  PRE_APPLICATION,
  type PreApplicationPhase,
} from "@/lib/pre-application-popup";

// 사전입력 팝업의 dismiss 와 분리해 본 접수 안내가 새로 노출되게 한다.
const STORAGE_KEY = `application-registration-popup-dismissed:${PRE_APPLICATION.ROUND_KEY}`;

// dismiss 여부(localStorage) 판단 — 표시 기간과 무관한 클라이언트 전용 사용자 선호.
// YouTubeLivePopup 과 동일한 1d/30d 로직.
function isDismissed(): boolean {
  const dismissed = localStorage.getItem(STORAGE_KEY);
  if (!dismissed) return false;

  const duration = localStorage.getItem(STORAGE_KEY + "-duration");

  // 30일 숨김 (분할된 본 접수 기간 전체 커버)
  if (duration === "30d") {
    const daysDiff =
      (Date.now() - new Date(dismissed).getTime()) / (24 * 60 * 60 * 1000);
    return daysDiff < 30;
  }

  // 당일 숨김
  const dismissedDate = new Date(dismissed);
  const today = new Date();
  return (
    dismissedDate.getFullYear() === today.getFullYear() &&
    dismissedDate.getMonth() === today.getMonth() &&
    dismissedDate.getDate() === today.getDate()
  );
}

export default function PreApplicationPopup() {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<PreApplicationPhase>("hidden");

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        // 표시 기간/변형은 서버 시각으로 판정한다(클라이언트 시계 비의존).
        const res = await fetch("/api/pre-application-popup");
        if (!res.ok) return;
        const data = (await res.json()) as { phase: PreApplicationPhase };
        if (cancelled || data.phase === "hidden") return;

        // dismiss 는 클라이언트(localStorage) 전용 판단
        if (isDismissed()) return;

        setPhase(data.phase);
        setOpen(true);
      } catch {
        // 네트워크 오류 시 팝업을 띄우지 않음
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  function dismiss(duration: "1d" | "30d") {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    localStorage.setItem(STORAGE_KEY + "-duration", duration);
    setOpen(false);
  }

  const content = getPopupContent(phase);
  const isUrgent = phase === "additionalOpen";

  if (!content) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-balance text-lg">
            {isUrgent ? (
              <AlertTriangle
                className="size-6 shrink-0 text-red-600 dark:text-red-400"
                aria-hidden="true"
              />
            ) : (
              <CalendarClock
                className="size-6 shrink-0 text-amber-500 dark:text-amber-400"
                aria-hidden="true"
              />
            )}
            {content.title}
          </DialogTitle>
          <DialogDescription>{PRE_APPLICATION.ROUND_LABEL}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{content.description}</p>

          <div
            className={
              isUrgent
                ? "rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/40"
                : "rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40"
            }
          >
            <div
              className={
                isUrgent
                  ? "text-xs font-medium text-red-700 dark:text-red-400"
                  : "text-xs font-medium text-amber-700 dark:text-amber-400"
              }
            >
              전체 원서접수 일정
            </div>
            <dl className="mt-3 space-y-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">
                  1차 접수
                </dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-foreground">
                  {PRE_APPLICATION.PRIMARY_PERIOD_LABEL}
                </dd>
              </div>

              <div className="rounded-md bg-background/70 px-3 py-2">
                <dt className="text-xs font-medium text-muted-foreground">
                  추석 연휴 큐넷 원서접수 서비스 중단
                </dt>
                <dd className="mt-0.5 font-medium tabular-nums text-foreground">
                  {PRE_APPLICATION.SERVICE_PAUSE_LABEL}
                </dd>
              </div>

              <div>
                <dt className="text-xs font-medium text-muted-foreground">
                  접수 재개
                </dt>
                <dd className="mt-0.5 font-semibold tabular-nums text-foreground">
                  {PRE_APPLICATION.RESUMED_PERIOD_LABEL}
                </dd>
              </div>
            </dl>
          </div>

          <a
            href={PRE_APPLICATION.QNET_HREF}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className={
              isUrgent
                ? "flex w-full touch-manipulation items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:bg-red-500 dark:hover:bg-red-600"
                : "flex w-full touch-manipulation items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
            }
          >
            큐넷에서 원서접수하기
            <ArrowRight className="size-4" aria-hidden="true" />
          </a>

          <Link
            href={PRE_APPLICATION.GUIDE_HREF}
            onClick={() => setOpen(false)}
            className="block rounded-sm text-center text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            원서접수 방법 확인하기
          </Link>
        </div>

        {/* 숨김 버튼 (YouTubeLivePopup 과 동일 영역/스타일) */}
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <button
            type="button"
            onClick={() => dismiss("1d")}
            className="touch-manipulation rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            당일 안내 보지 않기
          </button>
          <span aria-hidden="true">|</span>
          <button
            type="button"
            onClick={() => dismiss("30d")}
            className="touch-manipulation rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            이번 원서접수 동안 보지 않기
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getPopupContent(phase: PreApplicationPhase) {
  switch (phase) {
    case "beforeOpen":
      return {
        title: "9월 21일 14시, 실기 원서접수 시작!",
        description:
          "정보처리기사가 속한 기사 등급은 오전 10시가 아니라 오후 2시부터 접수할 수 있어요. 사전입력을 마쳤다면 큐넷 마이페이지에서 바로 이어서 접수하세요.",
      };
    case "open":
      return {
        title: "정처기 실기 원서접수 중이에요",
        description:
          "1차 접수는 9월 23일 18시에 잠시 중단되고, 추석 연휴 뒤 9월 28일 09시에 재개돼요. 원하는 시험장이 마감되기 전에 선택과 결제를 완료해 주세요.",
      };
    case "additionalBeforeOpen":
      return {
        title: "9월 28일 9시, 실기 원서접수 재개!",
        description:
          "추석 연휴로 잠시 중단됐던 원서접수가 오전 9시부터 다시 열려요. 큐넷에서 시험장 선택과 결제를 마쳐 주세요.",
      };
    case "additionalOpen":
      return {
        title: "18시 마감! 실기 원서접수",
        description:
          "9월 28일은 2026년 3회 실기 원서접수 마지막 날이에요. 오후 6시 전에 시험장 선택과 결제를 모두 완료해 주세요.",
      };
    case "hidden":
      return null;
  }
}
